/**
 * Notification reminders.
 *
 * We use only the browser Notification API. There is NO push server:
 * this means we can fire notifications while the app or a background
 * tab is open, but we CANNOT fire them when the browser is fully
 * closed. The Settings UI states this limitation honestly.
 *
 * Scheduling model:
 *   - `startReminderScheduler(state)` reads `{enabled, times: [HH:MM]}`
 *     from state and schedules a JS timer to the next reminder in the
 *     Asia/Dhaka timezone. On fire, we notify (if there are clicks
 *     left today) and reschedule to the next slot.
 *   - Anywhere in the app can call `startReminderScheduler` again with
 *     fresh state — it cancels the previous scheduler and re-arms.
 *   - `stopReminderScheduler()` cancels the currently-armed timer.
 */

import { statsApi } from "@/lib/stats-api";

/** Ask the browser for Notification permission if not already decided. */
export async function ensureNotificationPermission() {
  if (typeof Notification === "undefined") return "unsupported";
  if (Notification.permission === "granted") return "granted";
  if (Notification.permission === "denied") return "denied";
  try {
    const p = await Notification.requestPermission();
    return p;
  } catch {
    return "denied";
  }
}

let armedTimer = null;

export function stopReminderScheduler() {
  if (armedTimer) {
    window.clearTimeout(armedTimer);
    armedTimer = null;
  }
}

/**
 * Given "HH:MM" strings, find the next-upcoming Date object in
 * Asia/Dhaka. If all today's slots are past, roll to tomorrow.
 */
function nextReminderAt(times) {
  if (!times || times.length === 0) return null;
  const now = new Date();
  const dhakaNowParts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Dhaka",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(now);
  const get = (t) => dhakaNowParts.find((p) => p.type === t)?.value;
  const yyyy = get("year");
  const mm = get("month");
  const dd = get("day");
  const hh = parseInt(get("hour"), 10);
  const min = parseInt(get("minute"), 10);
  const nowMin = hh * 60 + min;

  // Convert Dhaka "YYYY-MM-DD HH:MM" to a UTC Date via a tz-aware offset.
  // Dhaka has no DST; it's a fixed +06:00. Doing the arithmetic in
  // minutes-of-day keeps the code readable and correct.
  const DHAKA_OFFSET_MIN = 6 * 60; // +06:00

  const asUtcAt = (year, month, day, hour, minute) => {
    const utcMinutes = hour * 60 + minute - DHAKA_OFFSET_MIN;
    // Build a UTC date from year/month/day and add the utc minutes.
    // Note: Date.UTC uses month index 0-based.
    return new Date(Date.UTC(+year, +month - 1, +day, 0, 0)) +
      utcMinutes * 60 * 1000;
  };

  const candidates = [];
  for (const t of times) {
    const [h, m] = t.split(":").map((x) => parseInt(x, 10));
    if (Number.isNaN(h) || Number.isNaN(m)) continue;
    const targetMin = h * 60 + m;
    if (targetMin > nowMin) {
      candidates.push(new Date(asUtcAt(yyyy, mm, dd, h, m)));
    }
  }
  if (candidates.length > 0) {
    candidates.sort((a, b) => a.getTime() - b.getTime());
    return candidates[0];
  }

  // No more today — schedule the earliest slot tomorrow.
  const [tH, tM] = times[0].split(":").map((x) => parseInt(x, 10));
  const tomorrowDay = parseInt(dd, 10) + 1;
  // Let Date normalize month rollovers.
  return new Date(asUtcAt(yyyy, mm, tomorrowDay, tH, tM));
}

/**
 * Compose the reminder body from the latest links data. Skips if all
 * targets are already met.
 */
async function composeReminderBody() {
  try {
    const stats = await statsApi.stats();
    // Sum "clicks left today" from the daily_totals_30d last row & per_link
    // We need today's per-link remaining. Get from /links.
    // (Simpler: hit /links directly for freshness.)
    const res = await fetch(`${window.location.origin}/api/links`);
    if (!res.ok) return null;
    const links = await res.json();
    if (!Array.isArray(links) || links.length === 0) return null;
    let remaining = 0;
    for (const l of links) {
      remaining += Math.max(0, (l.daily_target || 0) - (l.today_count || 0));
    }
    if (remaining === 0) return null; // don't nag when done
    const noun = remaining === 1 ? "click" : "clicks";
    return `${remaining} ${noun} still left today — you got this.`;
  } catch {
    return null;
  }
}

function showNotification(body) {
  if (typeof Notification === "undefined") return;
  if (Notification.permission !== "granted") return;
  try {
    new Notification("RefClick reminder", {
      body,
      icon: "/icons/icon-192.png",
      badge: "/icons/icon-192.png",
      tag: "refclick-reminder", // collapse duplicates
      renotify: true,
    });
  } catch {
    /* silently ignore */
  }
}

export function startReminderScheduler(state) {
  stopReminderScheduler();
  if (!state?.enabled) return;
  if (!Array.isArray(state.times) || state.times.length === 0) return;

  const next = nextReminderAt(state.times);
  if (!next) return;
  const ms = Math.max(1000, next.getTime() - Date.now());

  armedTimer = window.setTimeout(async () => {
    const body = await composeReminderBody();
    if (body) showNotification(body);
    // Re-arm for the next slot
    startReminderScheduler(state);
  }, ms);
}
